import React, { useState, useEffect, useRef } from 'react';
import { Activity, User, Droplet, Gift, Calendar, Phone, MapPin, Shield, Award, Star, Users, TrendingUp, Edit, Check, Camera, Lock, Save, AlertCircle, X, Upload } from 'react-feather';
import api from '../../../config/api';
import { toast } from 'react-hot-toast';

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    profileImage: null,
    
    bloodGroup: '',
    age: '',
    weight: '',
    gender: '',
    
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    
    hasDiseases: false,
    diseases: [],
    medications: [],
    
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactPhone: '',
    
    isAvailable: true,
    
    totalDonations: 0,
    pendingRequests: 0,
    completedDonations: 0,
    lastDonation: null
  });
  
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  useEffect(() => {
    fetchProfileData();
  }, []);

  // Fetch profile data from the backend
  const fetchProfileData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // First try to get detailed profile
      let userData = null;
      let profileData = null;
      let statsData = {};
      
      try {
        const detailedResponse = await api.get('/auth/detailed-profile');
        userData = detailedResponse.data.user;
        profileData = detailedResponse.data.profile;
        statsData = detailedResponse.data.stats || {};
        console.log('Detailed profile data loaded successfully');
      } catch (detailedError) {
        console.log('Detailed profile endpoint not available, falling back to regular endpoints');
        
        // If detailed profile fails, fall back to regular profile endpoints
        const userResponse = await api.get('/auth/profile');
        userData = userResponse.data;
        
        // Check if user has donor role (either as single role or in roles array)
        const isDonor = 
          userData.role === 'donor' || 
          (userData.roles && Array.isArray(userData.roles) && userData.roles.includes('donor'));
        
        // Try to get donor profile if user is a donor
        if (isDonor) {
          try {
            const donorResponse = await api.get('/api/donors/me');
            profileData = donorResponse.data;
          } catch (donorError) {
            console.error('Error fetching donor profile:', donorError);
          }
        }
      }
      
      // Map backend data to component state
      setProfileData({
        // User data
        fullName: userData?.name || '',
        email: userData?.email || '',
        phoneNumber: userData?.phone || '',
        profileImage: userData?.profileImage || null,
        
        // Donor data
        bloodGroup: profileData?.bloodType || '',
        age: profileData?.age || '',
        weight: profileData?.weight || '',
        gender: profileData?.gender || '',
        
        // Address
        addressLine1: profileData?.address?.street || '',
        addressLine2: '',
        city: profileData?.address?.city || '',
        state: profileData?.address?.state || '',
        postalCode: profileData?.address?.zipCode || '',
        country: profileData?.address?.country || '',
        
        // Medical history
        hasDiseases: profileData?.medicalHistory?.hasDiseases || false,
        diseases: profileData?.medicalHistory?.diseases || [],
        medications: profileData?.medicalHistory?.medications || [],
        
        // Emergency contact
        emergencyContactName: profileData?.emergencyContact?.name || '',
        emergencyContactRelationship: profileData?.emergencyContact?.relationship || '',
        emergencyContactPhone: profileData?.emergencyContact?.phone || '',
        
        // Availability
        isAvailable: profileData?.isAvailable !== undefined ? profileData.isAvailable : true,
        
        // Stats
        totalDonations: statsData?.totalDonations || profileData?.donationHistory?.length || 0,
        pendingRequests: statsData?.pendingRequests || 
          (profileData?.acceptedRequests?.filter(r => r.status === 'accepted' || r.status === 'pending')?.length || 0),
        completedDonations: statsData?.completedDonations || 
          (profileData?.acceptedRequests?.filter(r => r.status === 'donated')?.length || 0),
        lastDonation: statsData?.lastDonation || 
          (profileData?.donationHistory && profileData.donationHistory.length > 0 ? 
            profileData.donationHistory[profileData.donationHistory.length - 1].date : null)
      });
    } catch (err) {
      console.error('Error fetching profile data:', err);
      setError('Failed to load profile data. Please try again.');
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  // Handle image file selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG)');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }
    
    // Create a preview
    const reader = new FileReader();
    reader.onload = () => {
      // Create an image element to resize the image
      const img = new Image();
      img.onload = () => {
        // Create a canvas to resize the image
        const canvas = document.createElement('canvas');
        
        // Calculate new dimensions (max 500px width/height)
        let width = img.width;
        let height = img.height;
        const maxSize = 500;
        
        if (width > height && width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
        
        // Resize the image
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Get the resized image as a data URL with reduced quality
        const resizedImage = canvas.toDataURL('image/jpeg', 0.7);
        
        // Set the preview
        setImagePreview(resizedImage);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  // Save the uploaded image
  const saveProfileImage = async () => {
    if (!imagePreview) return;
    
    try {
      // Create a FormData object for the image upload
      const formData = new FormData();
      
      // Convert base64 to blob
      const fetchResponse = await fetch(imagePreview);
      const blob = await fetchResponse.blob();
      
      // Append the image file to the FormData
      formData.append('profileImage', blob, 'profile-image.jpg');
      formData.append('name', profileData.fullName);
      formData.append('email', profileData.email);
      formData.append('phone', profileData.phoneNumber);
      
      // Update profile with new image using FormData
      const response = await api.put('/auth/profile', {
        name: profileData.fullName,
        email: profileData.email,
        phone: profileData.phoneNumber,
        profileImage: imagePreview
      });
      
      // Update local state
      setProfileData(prev => ({
        ...prev,
        profileImage: imagePreview
      }));
      
      // Reset preview and close modal
      setImagePreview(null);
      setShowCameraModal(false);
      
      toast.success('Profile photo updated successfully!');
    } catch (err) {
      console.error('Error updating profile image:', err);
      toast.error('Failed to update profile photo. Please try a smaller image.');
    }
  };

  // Cancel image upload
  const cancelImageUpload = () => {
    setImagePreview(null);
    setShowCameraModal(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save profile data to the backend
  const saveProfile = async () => {
    setSaving(true);
    setError(null);
    
    try {
      // Prepare data for the backend
      const updateData = {
        // User data
        name: profileData.fullName,
        email: profileData.email,
        phone: profileData.phoneNumber,
        profileImage: profileData.profileImage,
        
        // Donor data
        bloodType: profileData.bloodGroup,
        age: parseInt(profileData.age),
        weight: parseInt(profileData.weight),
        gender: profileData.gender,
        
        // Address
        address: {
          street: profileData.addressLine1,
          city: profileData.city,
          state: profileData.state,
          zipCode: profileData.postalCode,
          country: profileData.country
        },
        
        // Medical history
        medicalHistory: {
          hasDiseases: profileData.hasDiseases,
          diseases: profileData.diseases,
          medications: profileData.medications
        },
        
        // Emergency contact
        emergencyContact: {
          name: profileData.emergencyContactName,
          relationship: profileData.emergencyContactRelationship,
          phone: profileData.emergencyContactPhone
        },
        
        // Availability
        isAvailable: profileData.isAvailable
      };
      
      // Send update request
      const response = await api.put('/auth/profile', updateData);
      
      toast.success('Profile updated successfully!');
      setIsEditing(false);
      
      // Refresh profile data
      fetchProfileData();
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEditing) {
      saveProfile();
    } else {
      setIsEditing(true);
    }
  };

  // Handle password change
  const handlePasswordChange = async (currentPassword, newPassword) => {
    try {
      await api.put('/auth/change-password', {
        currentPassword,
        newPassword
      });
      
      toast.success('Password changed successfully!');
      setShowPasswordModal(false);
    } catch (err) {
      console.error('Error changing password:', err);
      toast.error(err.response?.data?.message || 'Failed to change password');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl shadow-lg p-4 sm:p-6">
        {/* Profile Header Content */}
        <div className="flex flex-col gap-4">
          {/* Profile Info Section */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
            <div className="w-20 h-20 bg-white rounded-full shadow-lg flex items-center justify-center overflow-hidden shrink-0">
              {profileData.profileImage ? (
                <img 
                  src={profileData.profileImage} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-10 h-10 text-red-500" />
              )}
            </div>
            <div className="text-center sm:text-left">
              <h2 className="text-xl sm:text-2xl font-bold text-white">{profileData.fullName}</h2>
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-3 mt-2">
                <span className="px-3 py-1 bg-white/20 rounded-full text-sm text-white flex items-center gap-2">
                  <Droplet className="w-4 h-4" />
                  {profileData.bloodGroup || 'Not specified'}
                </span>
                <span className="px-3 py-1 bg-white/20 rounded-full text-sm text-white flex items-center gap-2">
                  <Gift className="w-4 h-4" />
                  {profileData.totalDonations} donations
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="w-full sm:w-auto px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent"></div>
                  Saving...
                </>
              ) : isEditing ? (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              ) : (
                <>
                  <Edit className="w-4 h-4" />
                  Edit Profile
                </>
              )}
            </button>
            <button
              onClick={() => setShowCameraModal(true)}
              className="w-full sm:w-auto px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              <Camera className="w-4 h-4" />
              <span className="sm:inline">Update Photo</span>
            </button>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="w-full sm:w-auto px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              <Lock className="w-4 h-4" />
              <span className="sm:inline">Change Password</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Error</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Column - Personal & Contact Info */}
        <div className="md:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-gray-500" />
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={profileData.fullName}
                  onChange={(e) => handleProfileChange('fullName', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Blood Group</label>
                <select
                  value={profileData.bloodGroup}
                  onChange={(e) => handleProfileChange('bloodGroup', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                <input
                  type="number"
                  value={profileData.age}
                  onChange={(e) => handleProfileChange('age', e.target.value)}
                  disabled={!isEditing}
                  min="18"
                  max="65"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg)</label>
                <input
                  type="number"
                  value={profileData.weight}
                  onChange={(e) => handleProfileChange('weight', e.target.value)}
                  disabled={!isEditing}
                  min="45"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select
                  value={profileData.gender}
                  onChange={(e) => handleProfileChange('gender', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Availability Status</label>
                <div className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    id="isAvailable"
                    checked={profileData.isAvailable}
                    onChange={(e) => handleProfileChange('isAvailable', e.target.checked)}
                    disabled={!isEditing}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isAvailable" className="ml-2 block text-sm text-gray-700">
                    Available for donation
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5 text-gray-500" />
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => handleProfileChange('email', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={profileData.phoneNumber}
                  onChange={(e) => handleProfileChange('phoneNumber', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-gray-500" />
              Address Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                <input
                  type="text"
                  value={profileData.addressLine1}
                  onChange={(e) => handleProfileChange('addressLine1', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={profileData.city}
                  onChange={(e) => handleProfileChange('city', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input
                  type="text"
                  value={profileData.state}
                  onChange={(e) => handleProfileChange('state', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                <input
                  type="text"
                  value={profileData.postalCode}
                  onChange={(e) => handleProfileChange('postalCode', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <input
                  type="text"
                  value={profileData.country}
                  onChange={(e) => handleProfileChange('country', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-gray-500" />
              Emergency Contact
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={profileData.emergencyContactName}
                  onChange={(e) => handleProfileChange('emergencyContactName', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                <input
                  type="text"
                  value={profileData.emergencyContactRelationship}
                  onChange={(e) => handleProfileChange('emergencyContactRelationship', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={profileData.emergencyContactPhone}
                  onChange={(e) => handleProfileChange('emergencyContactPhone', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Stats & Achievements */}
        <div className="space-y-6">
          {/* Donation Statistics */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-gray-500" />
              Donation Statistics
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Gift className="w-5 h-5 text-red-500" />
                  <span className="text-gray-700">Total Donations</span>
                </div>
                <span className="font-semibold">{profileData.totalDonations}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-red-500" />
                  <span className="text-gray-700">Last Donation</span>
                </div>
                <span className="font-semibold">
                  {profileData.lastDonation ? new Date(profileData.lastDonation).toLocaleDateString() : 'No donations yet'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-red-500" />
                  <span className="text-gray-700">Pending Requests</span>
                </div>
                <span className="font-semibold">{profileData.pendingRequests}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-red-500" />
                  <span className="text-gray-700">Completed Donations</span>
                </div>
                <span className="font-semibold">{profileData.completedDonations}</span>
              </div>
            </div>
          </div>

          {/* Medical Information */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-gray-500" />
              Medical Information
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="hasDiseases"
                    checked={profileData.hasDiseases}
                    onChange={(e) => handleProfileChange('hasDiseases', e.target.checked)}
                    disabled={!isEditing}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasDiseases" className="ml-2 block text-sm text-gray-700">
                    Has medical conditions
                  </label>
                </div>
                {profileData.hasDiseases && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Medical Conditions</label>
                    <textarea
                      value={profileData.diseases.join(', ')}
                      onChange={(e) => handleProfileChange('diseases', e.target.value.split(',').map(item => item.trim()))}
                      disabled={!isEditing}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                      placeholder="Enter medical conditions separated by commas"
                      rows="2"
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Medications</label>
                <textarea
                  value={profileData.medications.join(', ')}
                  onChange={(e) => handleProfileChange('medications', e.target.value.split(',').map(item => item.trim()))}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="Enter medications separated by commas"
                  rows="2"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Change Password</h3>
              <button 
                onClick={() => setShowPasswordModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <PasswordChangeForm onSubmit={handlePasswordChange} onCancel={() => setShowPasswordModal(false)} />
          </div>
        </div>
      )}

      {/* Photo Upload Modal */}
      {showCameraModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Update Profile Photo</h3>
              <button 
                onClick={cancelImageUpload}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {imagePreview ? (
                <div className="flex flex-col items-center">
                  <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-red-500">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Preview</p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <Upload className="w-10 h-10 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500 mb-2">Click to select or drag an image</p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageSelect}
                    accept="image/jpeg, image/png, image/jpg"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
              )}
              
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={cancelImageUpload}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={saveProfileImage}
                  disabled={!imagePreview}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Save Photo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Password Change Form Component
const PasswordChangeForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validate passwords
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await onSubmit(formData.currentPassword, formData.newPassword);
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="text-sm">{error}</p>
        </div>
      )}
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
        <input
          type="password"
          name="currentPassword"
          value={formData.currentPassword}
          onChange={handleChange}
          required
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
        <input
          type="password"
          name="newPassword"
          value={formData.newPassword}
          onChange={handleChange}
          required
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
        <input
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          required
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
        />
      </div>
      
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {loading ? 'Changing...' : 'Change Password'}
        </button>
      </div>
    </form>
  );
};

export default Profile;